import { parse } from 'date-fns';

/**
 * Разбор дат — перенос strToDate и списков форматов из AppFCFun.kt:229-323.
 *
 * Порядок перебора форматов сохранён: сначала форматы с временем, затем только дата.
 * Список и порядок важны — при совпадении нескольких форматов берётся первый.
 */
const DATE_TIME_FORMATS = [
  "yyyy-MM-dd'T'HH:mm:ss",
  "yyyy/MM/dd'T'HH:mm:ss",
  "yyyy.MM.dd'T'HH:mm:ss",
  'dd/MM/yyyy HH:mm:ss',
  'dd-MM-yyyy HH:mm:ss',
  'dd.MM.yyyy HH:mm:ss',
] as const;

/**
 * ⚠️ Формат `yy/MM/dd` в этом списке практически недостижим: раньше стоит `dd/MM/yy`,
 * и строка «26/08/17» успешно разбирается как 26 августа 2017 года. Так же ведёт себя
 * оригинал (AppFCFun.kt:262-276) — порядок форматов сохранён намеренно, потому что от него
 * зависит результат разбора. Формат включится только там, где `dd/MM/yy` не подходит,
 * например «32/08/17».
 */
const DATE_FORMATS = [
  'dd/MM/yy',
  'dd.MM.yy',
  'dd-MM-yy',
  'yy/MM/dd',
  'yyyy-MM-dd',
  'dd/MM/yyyy',
  'dd-MM-yyyy',
  'yyyy/MM/dd',
  'dd.MM.yyyy',
] as const;

/**
 * Опорная дата для разбора двузначного года: в kotlinx-datetime это
 * yearTwoDigits(baseYear = 2000), поэтому date-fns тоже должен считать от 2000 года.
 */
const TWO_DIGIT_YEAR_BASE = new Date(2000, 0, 1);

/**
 * Разбор значения ячейки в дату. Бросает исключение на неизвестном формате —
 * как оригинал (`throw IllegalArgumentException`), чтобы расхождение не прошло молча.
 */
export function strToDate(cellValue: string): Date {
  const hasTimeIndicators =
    cellValue.includes('T') || cellValue.includes(':') || cellValue.includes(' ');
  const isLikelyDateTime = cellValue.length > 10 || hasTimeIndicators;

  if (isLikelyDateTime) {
    for (const format of DATE_TIME_FORMATS) {
      const parsed = parse(cellValue, format, TWO_DIGIT_YEAR_BASE);
      if (!Number.isNaN(parsed.getTime())) return parsed;
    }
  }

  for (const format of DATE_FORMATS) {
    const parsed = parse(cellValue, format, TWO_DIGIT_YEAR_BASE);
    if (!Number.isNaN(parsed.getTime())) return parsed;
  }

  throw new Error(`Unsupported date format: '${cellValue}'`);
}

/** Компаратор колонок-дат — перенос dateComparator (AppFCFun.kt:320-323). */
export function dateComparator(date1: string, date2: string): number {
  return strToDate(date1).getTime() - strToDate(date2).getTime();
}

/**
 * Компаратор фильтра по дате — перенос filterParams (AppFCFun.kt:229-231).
 *
 * В оригинале дата из фильтра приводилась через toLocaleDateString(), то есть
 * результат зависит от локали браузера. Поведение сохранено как есть.
 */
export function filterParamsComparator(
  filterLocalDateAtMidnight: Date,
  cellValue: string,
): number {
  return dateComparator(cellValue, new Date(filterLocalDateAtMidnight).toLocaleDateString());
}

/** Список форматов — для тестов и диагностики. */
export const SUPPORTED_FORMATS = {
  dateTime: DATE_TIME_FORMATS,
  date: DATE_FORMATS,
  total: DATE_TIME_FORMATS.length + DATE_FORMATS.length,
} as const;
